import { FC, useCallback, useContext, useMemo } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import set from 'lodash/set';

import { OnboardingWizardContext } from '@modules/Onboarding/contexts/OnboardingWizardContext/OnboardingWizardContext';
import { StoryForm } from '@modules/Onboarding/partials/StoryForm/StoryForm';
import { StoryGuide } from './StoryGuide';


type Props = {
  onComplete: () => void;
  onBack?: () => void;
};

export const StoryStep: FC<Props> = (props) => {
  const { onComplete, onBack } = props;
  const { data, setData } = useContext(OnboardingWizardContext);

  const onStoryChange = useCallback((fieldPath: string, value: any) => {
    const updatedData = set({ ...data }, fieldPath, value);
    setData(updatedData);
  }, [data, setData]);

  const defaultValues = useMemo(() => {
    return {
      story: data?.story || '',
    };
  }, [data?.story]);

  return (
    <Box>
      <Stack gap={2} mb={4}>
        <Typography variant="body2" textAlign="center" fontSize={12} color="text.secondary" mb={3}>Final step! Share your story for personalised insights.</Typography>
        <Typography variant="body1" textAlign="center" fontSize={30} fontWeight={600}>
          Share your Leadership story
        </Typography>
        <Typography variant="body2" textAlign="center" color="text.secondary" fontSize={16}>
          Write a leadership story that you can use in presentations, meetings, or other communications.
        </Typography>
      </Stack>

      <StoryGuide/>

      <StoryForm
        defaultValues={defaultValues}
        onFieldChange={onStoryChange}
        onSubmitRequest={onComplete}
        slots={{
          footer: (formState) => (
            <Stack direction={{ xs: 'column-reverse', sm: 'row' }} alignItems="center" justifyContent="space-between" gap={2} mt={4}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack/>}
                size="large"
                onClick={onBack}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                type="submit"
                endIcon={<ArrowForward/>}
                size="large"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
                disabled={!formState.isValid || formState.isSubmitting}
              >
                {formState.isValid ? 'Analyze My Story' : 'Continue when ready'}
              </Button>
            </Stack>
          ),
        }}
      />
    </Box>
  );
};
